import {
  BaseQueryFn,
  BaseQueryMeta,
  BaseQueryResult,
  createApi,
  EndpointBuilder,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  retry,
  TagDescription,
} from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '@/api/common';
import { configuration } from '@/config';
import {
  createMockCustomerKYXVerificationsPage,
  getMockCustomerKYXVerification,
} from '@/data/mockCustomerKYXVerifications';
import {
  createMockTransactionDetails,
  getMockTransactionStatusLogs,
} from '@/data/mockTransactionDetails';
import { createMockTransactionsPage } from '@/data/mockTransactions';
import {
  CreateDashboardKYXVerificationRequest,
  DashboardKYXVerificationResponse,
} from '@/DTOs/dashboardKYXVerifications';
import { DownloadTransactionDocumentRequest } from '@/DTOs/downloadTransactionDocumentRequest';
import {
  DeleteFileRequest,
  DownloadFileRequest,
  FileMetadata,
  UploadFileRequest,
} from '@/DTOs/files';
import { CreateGroupRequest, Group, UpdateGroupRequest } from '@/DTOs/groups';
import { PaymentInstructionsRequest, PaymentInstructionsResponse } from '@/DTOs/paymentInstruction';
import { CreateTotpRequest, CreateTotpResponse } from '@/DTOs/totp';
import { UpdateDepositTransactionHashRequest } from '@/DTOs/updateDepositTransactionHash';
import { UpdateUserPreferencesRequest, UserPreferencesResponse } from '@/DTOs/userPreferences';
import { CreateUserRequest, UpdateUserRequest } from '@/DTOs/users';
import { WalletRequest, WalletResponse } from '@/DTOs/walletResponse';
import { mockPaymentInstructions } from '@/mocks/mockPaymentInstruction';
import {
  AddressAutocompleteRequest,
  AddressAutocompleteResponse,
  Place,
} from '@/types/addressValidation';
import {
  BankInfoRequest,
  BankInfoResponse,
  DetailedBalance,
  DetailedBalancesRequest,
  DetailedMultiCurrenciesBalancesRequest,
  IbanBankInfoResponse,
  SwiftBankInfoResponse,
} from '@/types/bankInfo';
import { BitsoBank } from '@/types/bitsoBank';
import { ConfigurationResponse } from '@/types/configuration';
import {
  Contact,
  CreateContactRequest,
  CreateFiatAccountRequest,
  CreateFiatAccountResponse,
  FiatAccount,
  UpdateContactRequest,
  UpdateFiatAccountRequest,
} from '@/types/contacts';
import { CurrencyCodeType, DashboardOperation, PaymentRailType } from '@/types/currency';
import { Customer } from '@/types/customer';
import { CustomerKYXVerification } from '@/types/customerKYXVerification';
import { GetCustomerKYXVerificationsFilters } from '@/types/getCustomerKYXVerificationsFilters';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { Nullable } from '@/types/nullable';
import { Page } from '@/types/page';
import { ProofOfPaymentType } from '@/types/proofOfPaymentType';
import { SimulationRequest, SimulationResponse } from '@/types/simulation';
import { SpeiBank } from '@/types/speiBank';
import {
  MockDepositRequest,
  SimplifiedTransactionStatus,
  Transaction,
  TransactionNotification,
  TransactionRequest,
  TransactionResponse,
  TransactionType,
  TransactionWithDocuments,
} from '@/types/transaction';
import { TransactionStatusLogResponse } from '@/types/transactionStatusLog';
import { GetUsersFilters, User } from '@/types/user';
import { extractFileNameFromContentDispositionHeader } from '@/utility/extractFileNameFromContentDispositionHeader';
import { resolveFromAccount, resolveToAccount } from '@/utility/transactionAccountResolvers';
import {
  customerFileRemovePatcher,
  customerFileUploadPatcher,
  isCustomerFilePath,
} from '@/utility/v1helpers';

const reducerPath = 'caliza.v1' as const;

const shouldMock = (): boolean => {
  return configuration.mock;
};

type TagTypes =
  | 'UserProfile'
  | 'UserPreferences'
  | 'Currencies'
  | 'Simulation'
  | 'Contacts'
  | 'Balances'
  | 'Transactions'
  | 'FiatAccounts'
  | 'Users'
  | 'Groups'
  | 'Configuration'
  | 'CustomerKYXVerifications';

const getInvalidateBalancesTag = (
  result: TransactionResponse | undefined,
): readonly TagDescription<TagTypes>[] => {
  if (result && result.from) {
    const { currencyCode } = result.from;
    return [{ type: 'Balances', id: currencyCode }, 'Transactions'];
  }

  return ['Balances', 'Transactions'];
};

const getBalancesTagDescription = (
  result: DetailedBalance | undefined,
): readonly TagDescription<TagTypes>[] =>
  result ? [{ type: 'Balances', id: result.currencyCode }] : ['Balances'];

type BaseQuery = ReturnType<typeof fetchBaseQuery>;
type ReducerPath = 'caliza-api';

const paramsSerializer = (params: Record<string, any>): string => {
  const entries = Object.entries(params);
  return entries
    .filter(([, value]: [string, any]): boolean => {
      if (value === undefined || value === null) {
        return false;
      } else if (Array.isArray(value)) {
        return value.length > 0;
      }

      return true;
    })
    .reduce(
      (
        reduced: ReadonlyArray<[string, any]>,
        [key, value]: [string, any],
      ): ReadonlyArray<[string, any]> => {
        if (Array.isArray(value)) {
          return [...reduced, ...value.map((value: any): [string, any] => [key, value])];
        }
        return [...reduced, [key, value]];
      },
      [],
    )
    .map(([key, value]: [string, any]): string => `${key}=${value}`)
    .join('&');
};

const query = fetchBaseQuery({
  baseUrl: configuration.calizaAPIBaseURL,
  paramsSerializer: paramsSerializer,
  prepareHeaders: prepareHeaders,
});

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  any,
  FetchBaseQueryMeta
> = async (
  args: string | FetchArgs,
  api: any,
  extraOptions: any,
): Promise<BaseQueryResult<any>> => {
  if (typeof args === 'string' && args === '') {
    return { data: undefined };
  } else if (typeof args === 'object' && args.url === '') {
    return { data: undefined };
  }

  return query(args, api, extraOptions);
};

export const calizaApi = createApi({
  baseQuery: retry(customBaseQuery, {
    /* Retry doesn't make any sense.
     *
     * If the request failed, there's a reason for that.
     */
    maxRetries: 0,
  }),
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  endpoints: (build: EndpointBuilder<BaseQuery, TagTypes, ReducerPath>) => {
    return {
      bankLookup: build.query<BankInfoResponse, BankInfoRequest | undefined>({
        query: (request: BankInfoRequest | undefined): FetchArgs => {
          return {
            url: request
              ? `/v1/bank-info/${request.routingNumber}?paymentType=${request.paymentType}`
              : '',
          };
        },
      }),
      swiftCodeLookup: build.query<SwiftBankInfoResponse, string>({
        query: (swiftCode: string): FetchArgs => {
          return {
            url: `/v1/bank-info/swift/${swiftCode}`,
          };
        },
      }),
      ibanCodeLookup: build.query<IbanBankInfoResponse, string>({
        query: (ibanCode: string): FetchArgs => {
          return {
            url: `/v1/bank-info/iban/${ibanCode}`,
          };
        },
      }),
      getPlaceDetails: build.query<Place, string>({
        query: (placeId: string): FetchArgs => {
          return {
            url: `/v1/addresses/places/${placeId}`,
          };
        },
      }),
      autocompleteAddress: build.query<AddressAutocompleteResponse, AddressAutocompleteRequest>({
        query: (req: AddressAutocompleteRequest): FetchArgs => ({
          url: `/v1/addresses/autocomplete`,
          params: {
            country: req.country,
            address: req.address,
          },
        }),
      }),
      createContact: build.mutation<Contact, CreateContactRequest>({
        query: (body: CreateContactRequest): FetchArgs => ({
          url: '/v1/contacts',
          method: 'POST',
          body: body,
        }),
        invalidatesTags: ['Contacts'],
      }),
      createFakeSpeiPayment: build.mutation<void, MockDepositRequest>({
        query: (request: MockDepositRequest): FetchArgs => ({
          url: '/v1/transactions/sandbox/spei',
          method: 'POST',
          body: request,
        }),
      }),
      createFakePIXPayment: build.mutation<void, MockDepositRequest>({
        query: (request: MockDepositRequest): FetchArgs => ({
          url: '/v1/transactions/sandbox/pix',
          method: 'POST',
          body: request,
        }),
      }),
      createFiatAccount: build.mutation<CreateFiatAccountResponse, CreateFiatAccountRequest>({
        query: (body: CreateFiatAccountRequest): FetchArgs => ({
          url: '/v1/fiat-accounts',
          method: 'POST',
          body: body,
        }),
        invalidatesTags: ['FiatAccounts', 'Contacts'],
      }),
      createIntegrator: build.mutation<any, void>({
        query: (): FetchArgs => ({
          url: '/v1/customer-signup/integrator',
          method: 'POST',
        }),
      }),
      createSimulation: build.mutation<SimulationResponse, Nullable<SimulationRequest>>({
        query: (request: Nullable<SimulationRequest>): FetchArgs => ({
          url: '/v1/simulations',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Simulation'],
      }),
      createTransaction: build.mutation<TransactionResponse, TransactionRequest>({
        query: (transactionRequest: TransactionRequest): FetchArgs => {
          const { attachment, ...request } = transactionRequest;
          const formData = new FormData();

          if (attachment) {
            formData.append('files', attachment);
          }
          formData.append('request', JSON.stringify(request));

          return {
            url: '/v1/transactions/:withDocuments',
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: getInvalidateBalancesTag,
      }),
      deleteContact: build.mutation<void, string>({
        query: (id: string): FetchArgs => ({
          url: `/v1/contacts/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Contacts'],
      }),
      deleteFiatAccount: build.mutation<void, string>({
        query: (id: string): FetchArgs => ({
          url: `/v1/fiat-accounts/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['FiatAccounts', 'Contacts'],
      }),
      deleteFile: build.mutation<unknown, DeleteFileRequest>({
        query: ({ path, id }: DeleteFileRequest): FetchArgs => {
          return {
            url: `/v1/customer-signup/${path}/${id}`,
            method: 'DELETE',
          };
        },
        onQueryStarted: async (
          request: DeleteFileRequest,
          // React toolkit is a stupid toolkit that thinks
          // it knows better. And that type inference is better
          // than explicitly stating what thing is what type.
          //
          // IMHO this makes having types completely pointless.
          //
          // eslint-disable-next-line @typescript-eslint/typedef
          { dispatch, queryFulfilled },
        ): Promise<void> => {
          if (isCustomerFilePath(request.path)) {
            const { util } = calizaApi;

            const patchResult = dispatch(
              util.updateQueryData('getCustomer', undefined, customerFileRemovePatcher(request)),
            );

            try {
              await queryFulfilled;
            } catch (error: unknown) {
              console.debug(error);
              // TODO: Dispatch an action to indicate there was an error and perhaps
              //       give feedback to the user.
              patchResult.undo();
            }
          }
        },
      }),
      downloadFile: build.query<string, DownloadFileRequest>({
        query: ({ path, id }: DownloadFileRequest): FetchArgs => ({
          url: `/v1/customer-signup/${path}/${id}`,
          method: 'GET',
          responseHandler: (response: Response): Promise<string> => response.text(),
        }),
      }),
      downloadFileInfo: build.query<FileMetadata, DownloadFileRequest>({
        query: ({ path, id }: DownloadFileRequest): FetchArgs => ({
          url: `/v1/customer-signup/${path}/${id}`,
          method: 'HEAD',
        }),
        transformResponse: async (
          _: BaseQueryResult<BaseQuery>,
          meta: BaseQueryMeta<BaseQuery>,
        ): Promise<FileMetadata> => {
          if (!meta) {
            throw new Error('No meta in redux toolkit fetch response');
          }

          const { response } = meta;
          if (!response) {
            throw new Error('No response in redux toolkit fetch response');
          }

          const { headers } = response;

          return {
            size: Number(headers.get('content-length')),
            mimeType: headers.get('content-type'),
            name: extractFileNameFromContentDispositionHeader(headers.get('content-disposition')),
          };
        },
      }),
      getBeneficiaryWallet: build.query<WalletResponse, WalletRequest>({
        query: (request: WalletRequest): string =>
          `/v1/beneficiaries/${request.beneficiaryId}/wallets/${request.chain}`,
      }),
      getBitsoBanksByCountryCode: build.query<readonly BitsoBank[], string>({
        query: (countryCode: string): string => `/v1/bitso/banks/${countryCode}`,
      }),
      getSpeiBanksByName: build.query<SpeiBank[], string>({
        query: (bankName: string): FetchArgs => ({
          url: `/v1/bank/spei/search`,
          params: {
            bankName,
          },
        }),
      }),
      getContacts: build.query<readonly Contact[], string | null>({
        query: (beneficiaryId: string | null): FetchArgs => ({
          url: beneficiaryId ? `/v1/contacts?beneficiaryId=${beneficiaryId}` : '',
        }),
        providesTags: ['Contacts'],
      }),
      getCustomer: build.query<Customer, void>({
        query: (): string => '/v1/customer-signup',
        providesTags: ['UserProfile'],
      }),
      getCustomerKYXVerifications: build.query<
        Page<CustomerKYXVerification>,
        GetCustomerKYXVerificationsFilters
      >({
        query: (params: GetCustomerKYXVerificationsFilters): FetchArgs => {
          // Return empty URL for mock data in development mode
          if (shouldMock()) {
            return { url: '' };
          }

          const {
            type,
            name,
            verificationStatus,
            verificationResult,
            fromDate,
            toDate,
            page = 1,
            size = 10,
            sort = ['createdDate', 'desc'],
          } = params;

          return {
            url: '/v1/dashboard-kyx-verifications',
            params: {
              type: type,
              name: name,
              verificationStatus: verificationStatus,
              verificationResult: verificationResult,
              fromDate: fromDate,
              toDate: toDate,
              page: page - 1,
              size: size,
              sort: sort,
            },
          };
        },
        transformResponse: async (
          page: Page<CustomerKYXVerification> | undefined,
          _meta: unknown,
          params: GetCustomerKYXVerificationsFilters,
        ): Promise<Page<CustomerKYXVerification>> => {
          // Return mock data in development mode
          if (shouldMock()) {
            const { page: pageNumber = 1, size = 10 } = params;

            return createMockCustomerKYXVerificationsPage(pageNumber, size);
          }

          if (!page) {
            throw new Error('Customer KYX verifications data not found');
          }

          return page;
        },
        providesTags: (
          result: Page<CustomerKYXVerification> | undefined,
        ): readonly TagDescription<TagTypes>[] =>
          result
            ? [
                { type: 'CustomerKYXVerifications', id: 'LIST' },
                ...result.content.map(
                  ({ id }: CustomerKYXVerification): TagDescription<TagTypes> =>
                    ({ type: 'CustomerKYXVerifications', id }) as const,
                ),
              ]
            : [{ type: 'CustomerKYXVerifications', id: 'LIST' }],
      }),
      getCustomerKYXVerification: build.query<CustomerKYXVerification, string>({
        query: (id: string): FetchArgs => {
          // Return empty URL for mock data in development mode
          if (shouldMock()) {
            return { url: '' };
          }

          return {
            url: `/v1/dashboard-kyx-verifications/${id}`,
          };
        },
        transformResponse: async (
          customer: CustomerKYXVerification | undefined,
          _meta: unknown,
          id: string,
        ): Promise<CustomerKYXVerification> => {
          // Return mock data in development mode
          if (shouldMock()) {
            const mockCustomer = getMockCustomerKYXVerification(id);

            if (!mockCustomer) {
              throw new Error(`Customer with ID ${id} not found`);
            }
            return mockCustomer;
          }

          if (!customer) {
            throw new Error('Customer KYX verification data not found');
          }

          return customer;
        },
        providesTags: (
          _result: CustomerKYXVerification | undefined,
          _error: FetchBaseQueryError | undefined,
          id: string,
        ): readonly TagDescription<TagTypes>[] => [{ type: 'CustomerKYXVerifications', id }],
      }),
      createCustomerKYXVerification: build.mutation<
        DashboardKYXVerificationResponse,
        CreateDashboardKYXVerificationRequest
      >({
        query: (body: CreateDashboardKYXVerificationRequest): FetchArgs => ({
          url: '/v1/dashboard-kyx-verifications',
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'CustomerKYXVerifications', id: 'LIST' }],
      }),
      getConfiguration: build.query<ConfigurationResponse, void>({
        query: (): string => '/v1/configuration',
        providesTags: ['Configuration'],
        keepUnusedDataFor: 60 * 60 * 24, // 24 hours,
      }),
      getDashboardOperations: build.query<
        readonly DashboardOperation[],
        'DEPOSIT' | 'TRANSFER' | 'WITHDRAWAL' | 'EXCHANGE'
      >({
        query: (operation: 'DEPOSIT' | 'TRANSFER' | 'WITHDRAWAL' | 'EXCHANGE'): string =>
          `/v1/flow-of-funds/dashboard/operations/${operation}`,
        providesTags: ['Currencies'],
      }),
      getDetailedMultiCurrencyBalances: build.query<
        readonly DetailedBalance[],
        DetailedMultiCurrenciesBalancesRequest
      >({
        query: (request: DetailedMultiCurrenciesBalancesRequest): FetchArgs => ({
          url: `/v1/beneficiaries/${request.beneficiaryId}/multi-currencies-balances/details`,
          params: {
            currency: request.currency,
          },
        }),
        providesTags: ['Balances'],
        keepUnusedDataFor: 0,
      }),
      getDetailedBalance: build.query<DetailedBalance, DetailedBalancesRequest>({
        query: (request: DetailedBalancesRequest): FetchArgs => ({
          url: `/v1/beneficiaries/${request.beneficiaryId}/balances/details`,
          params: {
            currency: request.currency,
          },
        }),
        providesTags: getBalancesTagDescription,
        keepUnusedDataFor: 0,
      }),
      getFiatAccount: build.query<FiatAccount, string>({
        query: (id: string | null): string => `/v1/fiat-accounts/${id}`,
        providesTags: (result: FiatAccount | undefined): readonly TagDescription<TagTypes>[] => [
          { type: 'FiatAccounts', id: result?.id },
        ],
      }),
      getFiatAccounts: build.query<Page<FiatAccount>, string | null>({
        query: (beneficiaryId: string | null): string =>
          beneficiaryId ? `/v1/fiat-accounts/beneficiaries/${beneficiaryId}` : '',
        providesTags: ['FiatAccounts'],
      }),
      getSimulation: build.query<SimulationResponse, string | null>({
        query: (simulationId: string | null): FetchArgs => ({
          url: `/v1/simulations/${simulationId}`,
        }),
        providesTags: ['Simulation'],
      }),
      getTransaction: build.query<TransactionResponse, string>({
        query: (transactionId: string): string => `/v1/transactions/${transactionId}`,
      }),
      downloadTransactionDetails: build.query<void, DownloadTransactionDocumentRequest>({
        query: (request: DownloadTransactionDocumentRequest): FetchArgs => ({
          url: `/v1/transactions/${request.transactionId}/proof-of-payment`,
          params: { type: request.type ?? ProofOfPaymentType.PAYMENT_CONFIRMATION },
          responseHandler: async (response: Response): Promise<void> => {
            if (response.status > 199 && response.status < 300) {
              const blob = await response.blob();
              const contentDisposition = response.headers.get('content-disposition');
              const filename = extractFileNameFromContentDispositionHeader(contentDisposition);

              const link = document.createElement('a');

              link.href = URL.createObjectURL(blob);
              link.download = filename ?? 'transaction-details.pdf';
              link.click();

              URL.revokeObjectURL(link.href);
            }
          },
        }),
      }),
      getTransactionDetails: build.query<TransactionWithDocuments, string>({
        query: (transactionId: string): string => {
          // Return empty URL for mock data in development mode
          if (shouldMock()) {
            return '';
          }
          return `/v1/transactions/${transactionId}/:withDocuments`;
        },
        transformResponse: async (
          transaction: TransactionWithDocuments | undefined,
          _meta: unknown,
          transactionId: string,
        ): Promise<TransactionWithDocuments> => {
          // Return mock data in development mode
          if (shouldMock()) {
            const mockTransaction = createMockTransactionDetails(transactionId);
            if (!mockTransaction) {
              throw new Error(`Transaction ${transactionId} not found`);
            }
            return mockTransaction;
          }

          if (!transaction) {
            throw new Error('Transaction data not found');
          }

          return {
            ...transaction,
            fromAccount: resolveFromAccount(transaction),
            toAccount: resolveToAccount(transaction),
            documents: transaction.documents || [],
          };
        },
        providesTags: ['Transactions'],
      }),
      getTransactionStatus: build.query<readonly TransactionNotification[], string | null>({
        query: (transactionId: string | null): FetchArgs => ({
          url: `/v1/transactions/${transactionId}/notifications`,
        }),
      }),
      getTransactionStatusLogs: build.query<TransactionStatusLogResponse, string>({
        query: (transactionId: string): FetchArgs => {
          // Return empty URL for mock data in development mode
          if (shouldMock()) {
            return { url: '' };
          }
          return { url: `/v1/transactions/${transactionId}/status-logs` };
        },
        transformResponse: async (
          statusLogs: TransactionStatusLogResponse | undefined,
          _meta: unknown,
          transactionId: string,
        ): Promise<TransactionStatusLogResponse> => {
          // Return mock data in development mode
          if (shouldMock()) {
            return getMockTransactionStatusLogs(transactionId);
          }

          if (!statusLogs) {
            throw new Error('Status logs data not found');
          }

          return statusLogs;
        },
      }),
      getTransactions: build.query<Page<Transaction>, Partial<GetTransactionsFilters>>({
        providesTags: ['Transactions'],
        query: (filters: Partial<GetTransactionsFilters>): FetchArgs => {
          // Return mock data in development mode
          if (shouldMock()) {
            return { url: '' }; // Empty URL will be handled by customBaseQuery to return undefined
          }

          const {
            id,
            amount,
            type,
            sort,
            status,
            fromDate,
            toDate,
            page = 1,
            size,
            currency,
            recipientId,
          } = filters;

          return {
            url: `/v1/transactions/`,
            params: {
              id: id,
              dashboard: true,
              amount: amount,
              page: page - 1,
              size: size,
              type: type === TransactionType.ALL ? undefined : type,
              status: status === SimplifiedTransactionStatus.ALL ? undefined : status,
              fromDate: fromDate ? fromDate : undefined,
              toDate: toDate ? toDate : undefined,
              currency: currency === CurrencyCodeType.ANY ? undefined : currency,
              recipientId: recipientId === '' ? undefined : recipientId,
              sort: sort,
            },
          };
        },
        transformResponse: async (
          page: Page<Transaction>,
          _meta: unknown,
          filters: Partial<GetTransactionsFilters>,
        ): Promise<Page<Transaction>> => {
          // Return mock data in development mode
          if (shouldMock()) {
            const { page: pageNum = 0, size = 20 } = filters;
            return createMockTransactionsPage(pageNum, size);
          }

          const { content } = page;

          return {
            ...page,
            content: content.map((transaction: Transaction): Transaction => {
              return {
                ...transaction,
                fromAccount: resolveFromAccount(transaction),
                toAccount: resolveToAccount(transaction),
              };
            }),
          };
        },
      }),
      getTransactionsCSV: build.query<
        {
          readonly data: string;
          readonly filename: string;
        },
        Partial<GetTransactionsFilters>
      >({
        query: (filters: Partial<GetTransactionsFilters>): FetchArgs => {
          const {
            type,
            status,
            fromDate,
            toDate,
            recipientId,
            currency,
            sort,
            page = 1,
            size,
          } = filters;

          return {
            url: `/v1/transactions`,
            responseHandler: (response: Response): Promise<string> => response.text(),
            params: {
              dashboard: true,
              csv: true,
              page: page - 1,
              size: size,
              type: type === TransactionType.ALL ? undefined : type,
              status: status === SimplifiedTransactionStatus.ALL ? undefined : status,
              fromDate: fromDate ? fromDate : undefined,
              toDate: toDate ? toDate : undefined,
              currency: currency === CurrencyCodeType.ANY ? undefined : currency,
              recipientId: recipientId === '' ? undefined : recipientId,
              sort: sort,
            },
          };
        },
        transformResponse: async (
          data: string,
          meta?: BaseQueryMeta<BaseQuery>,
        ): Promise<{
          readonly filename: string;
          readonly data: string;
        }> => {
          const contentDisposition = meta?.response?.headers?.get('content-disposition') ?? '';
          const filename =
            extractFileNameFromContentDispositionHeader(contentDisposition) || 'transactions.csv';

          return {
            filename: filename,
            data: data,
          };
        },
        providesTags: ['Transactions'],
      }),
      getVirtualAccount: build.query<PaymentInstructionsResponse, PaymentInstructionsRequest>({
        query: (request: PaymentInstructionsRequest): string => {
          if (shouldMock()) {
            return '';
          }
          return `/v1/payments/payment-instructions?beneficiaryId=${request.beneficiaryId}&paymentRail=${request.paymentRail}`;
        },
        transformResponse: (
          response: PaymentInstructionsResponse | undefined,
          _meta: unknown,
          request: PaymentInstructionsRequest,
        ): PaymentInstructionsResponse => {
          if (shouldMock()) {
            return (
              mockPaymentInstructions[request.paymentRail] ||
              mockPaymentInstructions[PaymentRailType.ACH]
            );
          }
          if (!response) {
            throw new Error('No payment instructions found');
          }
          return response;
        },
      }),
      updateContact: build.mutation<Contact, UpdateContactRequest>({
        query: (request: UpdateContactRequest): FetchArgs => ({
          url: `/v1/contacts/${request.id}`,
          method: 'PUT',
          body: request.data,
        }),
        invalidatesTags: ['Contacts'],
      }),
      updateDepositTransactionHash: build.mutation<
        TransactionResponse,
        UpdateDepositTransactionHashRequest
      >({
        query: (request: UpdateDepositTransactionHashRequest): FetchArgs => ({
          url: `/v1/transactions/${request.transactionId}/depositTransactionHash`,
          method: 'PUT',
          body: {
            transactionHash: request.transactionHash,
          },
        }),
      }),
      updateFiatAccount: build.mutation<CreateFiatAccountResponse, UpdateFiatAccountRequest>({
        query: (request: UpdateFiatAccountRequest): FetchArgs => ({
          url: `/v1/fiat-accounts/${request.id}`,
          method: 'PUT',
          body: request.data,
        }),
        invalidatesTags: ['FiatAccounts', 'Contacts'],
      }),
      getUserPreferences: build.query<UserPreferencesResponse, void>({
        query: (): string => '/v1/preferences',
        providesTags: ['UserPreferences'],
      }),
      resendInvitation: build.mutation<unknown, string>({
        query: (userId: string): FetchArgs => ({
          url: `/v1/users/${userId}/invite`,
          method: 'POST',
        }),
      }),
      createUsers: build.mutation<User, CreateUserRequest>({
        query: (request: CreateUserRequest): FetchArgs => ({
          url: '/v1/users',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Users'],
      }),
      updateUsers: build.mutation<User, UpdateUserRequest>({
        query: (request: UpdateUserRequest): FetchArgs => {
          const { id, ...body } = request;

          return {
            url: `/v1/users/${id}`,
            method: 'PUT',
            body: body,
          };
        },
        invalidatesTags: ['Users'],
      }),
      deleteUsers: build.mutation<void, string>({
        query: (userId: string): FetchArgs => ({
          url: `/v1/users/${userId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Users'],
      }),
      getUserByEmail: build.query<User, string>({
        query: (email: string): FetchArgs => ({
          url: `/v1/users/?email=${email}`,
        }),
        providesTags: ['Users'],
      }),
      getUsers: build.query<Page<User>, Partial<GetUsersFilters> | void>({
        query: (filters: Partial<GetUsersFilters> = {}): FetchArgs => {
          const { page = 1, size = 15, search, role, status, department } = filters;

          return {
            url: '/v1/users',
            params: {
              page: page - 1,
              size: size,
              search: search,
              role: role,
              status: status,
              department: department,
            },
          };
        },
        providesTags: ['Users'],
      }),
      updateUserPreferences: build.mutation<UserPreferencesResponse, UpdateUserPreferencesRequest>({
        query: (request: UpdateUserPreferencesRequest): FetchArgs => ({
          url: '/v1/preferences',
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: ['UserPreferences'],
      }),
      updateThemePreferences: build.mutation<void, { theme: string }>({
        query: (request: { theme: string }): FetchArgs => ({
          url: '/v1/preferences/theme',
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: ['UserPreferences'],
      }),
      createTotpCredential: build.mutation<CreateTotpResponse, CreateTotpRequest>({
        query: (request: CreateTotpRequest): FetchArgs => ({
          url: '/v1/preferences/totp',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['UserPreferences'],
      }),
      uploadFile: build.mutation<unknown, UploadFileRequest>({
        query: ({ path, file }: UploadFileRequest): FetchArgs => {
          const body = new FormData();
          body.append('file', file);

          return {
            url: `/v1/customer-signup/${path}`,
            method: 'POST',
            body: body,
          };
        },
        onQueryStarted: async (
          request: UploadFileRequest,
          // React toolkit is a stupid toolkit that thinks
          // it knows better. And that type inference is better
          // than explicitly stating what thing is what type.
          //
          // IMHO this makes having types completely pointless.
          //
          // eslint-disable-next-line @typescript-eslint/typedef
          { dispatch, queryFulfilled },
        ): Promise<void> => {
          if (isCustomerFilePath(request.path)) {
            const { util } = calizaApi;

            const patchResult = dispatch(
              util.updateQueryData('getCustomer', undefined, customerFileUploadPatcher(request)),
            );

            try {
              await queryFulfilled;
            } catch {
              // TODO: Dispatch an action to indicate there was an error and perhaps
              //       give feedback to the user.
              patchResult.undo();
            }
          }
        },
      }),
      getGroups: build.query<readonly Group[], void>({
        query: (): FetchArgs => ({
          url: '/v1/groups',
        }),
        providesTags: ['Groups'],
      }),
      createGroup: build.mutation<Group, CreateGroupRequest>({
        query: (request: CreateGroupRequest): FetchArgs => ({
          url: '/v1/groups',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Groups'],
      }),
      updateGroup: build.mutation<Group, UpdateGroupRequest>({
        query: (request: UpdateGroupRequest): FetchArgs => {
          const { groupId, ...body } = request;
          return {
            url: `/v1/groups/${groupId}`,
            method: 'PUT',
            body: body,
          };
        },
        invalidatesTags: ['Groups'],
      }),
    };
  },
  reducerPath: reducerPath,
  tagTypes: [
    'UserProfile',
    'UserPreferences',
    'Currencies',
    'Simulation',
    'Contacts',
    'Balances',
    'Transactions',
    'FiatAccounts',
    'Users',
    'Groups',
    'Configuration',
    'CustomerKYXVerifications',
  ],
});

export const {
  useGetBeneficiaryWalletQuery,
  useGetTransactionQuery,
  useGetVirtualAccountQuery,
  useUpdateDepositTransactionHashMutation,
  useGetCustomerQuery,
  useGetCustomerKYXVerificationsQuery,
  useGetCustomerKYXVerificationQuery,
  useCreateCustomerKYXVerificationMutation,
  useGetConfigurationQuery,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  useCreateTotpCredentialMutation,
  useUploadFileMutation,
  useDeleteFileMutation,
  useDeleteFiatAccountMutation,
  useDownloadFileInfoQuery,
  useDownloadFileQuery,
  useGetDashboardOperationsQuery,
  useCreateSimulationMutation,
  useCreateIntegratorMutation,
  useCreateTransactionMutation,
  useGetTransactionStatusQuery,
  useGetTransactionStatusLogsQuery,
  useCreateFakePIXPaymentMutation,
  useCreateFakeSpeiPaymentMutation,
  useGetSimulationQuery,
  useGetContactsQuery,
  useDeleteContactMutation,
  useCreateContactMutation,
  useCreateFiatAccountMutation,
  useBankLookupQuery,
  useGetSpeiBanksByNameQuery,
  useSwiftCodeLookupQuery,
  useLazyIbanCodeLookupQuery,
  useGetDetailedBalanceQuery,
  useGetDetailedMultiCurrencyBalancesQuery,
  useUpdateFiatAccountMutation,
  useGetFiatAccountQuery,
  useUpdateContactMutation,
  useGetFiatAccountsQuery,
  useGetTransactionsQuery,
  useGetTransactionDetailsQuery,
  useLazyGetTransactionsCSVQuery,
  useGetBitsoBanksByCountryCodeQuery,
  useLazyDownloadTransactionDetailsQuery,
  useLazyAutocompleteAddressQuery,
  useLazyGetPlaceDetailsQuery,
  useGetUsersQuery,
  useCreateUsersMutation,
  useUpdateUsersMutation,
  useDeleteUsersMutation,
  useResendInvitationMutation,
  useGetGroupsQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useGetUserByEmailQuery,
} = calizaApi;

// Gather all endpoints here that we need to export selectors for
const { getCustomer, getDashboardOperations, getUserPreferences, getTransactionStatusLogs } =
  calizaApi.endpoints;

// Export selectors for each endpoint
export const transactionFlowsSelector = getDashboardOperations.select;
export const customerSelector = getCustomer.select();
export const preferencesSelector = getUserPreferences.select();
export const statusLogsSelector = getTransactionStatusLogs.select;
